import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  LayoutDashboard, FolderOpen, ClipboardList, BarChart3,
  Users, Shield, Bell, LogOut, ChevronLeft, ChevronRight, Menu, X, Brain, FileSliders, FileBarChart, GitCompare, BookOpen, Settings, CalendarCheck, FileText, Search, Globe, Award, UserCheck, Kanban
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isLeadership, hasFullReadAccess, isAdminOnly, canManageTasks } from '../utils/permissions';
import { changeLanguage } from '../utils/i18nHelpers';
import api from '../services/api';
import './Layout.css';

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation(['navigation', 'common']);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar_collapsed') === 'true'
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);

  // Глобальный поиск
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const leadership = isLeadership(user);
  const fullRead = hasFullReadAccess(user);
  const adminOnly = isAdminOnly(user);
  const canTasks = canManageTasks(user);
  const dashboardPath = leadership ? '/dashboard/admin' : '/dashboard/department';

  const logoText = i18n.language === 'uz' ? 'YTMB' : 'ЕАБД';

  const menuItems = [
    { path: dashboardPath, icon: LayoutDashboard, label: t('dashboard'), match: '/dashboard' },
    { path: '/documents', icon: FolderOpen, label: t('documents'), match: '/documents' },
    { path: '/plans', icon: ClipboardList, label: t('planning'), match: '/plans' },
    ...(fullRead ? [
      { path: '/analytics', icon: BarChart3, label: t('analytics'), match: '/analytics' },
      { path: '/ai-assistant', icon: Brain, label: t('aiAssistant'), match: '/ai-assistant' },
      { path: '/presentations', icon: FileSliders, label: t('presentations'), match: '/presentations' },
      { path: '/reports/constructor', icon: FileBarChart, label: t('reportConstructor'), match: '/reports/constructor' },
      { path: '/weekly-control', icon: CalendarCheck, label: t('weeklyControl'), match: '/weekly-control' },
      { path: '/information-constructor', icon: FileText, label: t('infoConstructor'), match: '/information-constructor' },
      { path: '/analytics/compare', icon: GitCompare, label: t('periodComparison'), match: '/analytics/compare' },
      { path: '/hr', icon: UserCheck, label: i18n.language === 'uz' ? 'Kadrlar bo\'limi' : 'Отдел кадров', match: '/hr' },
    ] : []),
    ...(adminOnly ? [
      { path: '/directories', icon: BookOpen, label: t('directories'), match: '/directories' },
      { path: '/users', icon: Users, label: t('users'), match: '/users' },
      { path: '/audit-log', icon: Shield, label: t('auditLog'), match: '/audit-log' },
      { path: '/settings', icon: Settings, label: t('settings'), match: '/settings' },
    ] : []),
    { path: '/kpi', icon: Award, label: 'KPI сотрудников', match: '/kpi' },
    {
      path: canTasks ? '/tasks' : '/tasks/my',
      icon: Kanban,
      label: 'Проекты и задачи',
      match: '/tasks',
    },
    { path: '/notifications', icon: Bell, label: t('notifications'), match: '/notifications' },
  ];

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.getUnreadCount();
      setNotificationCount(res.data?.count || 0);
    } catch {
      // Ошибка — не показываем badge
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Debounce поиска
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.globalSearch(searchQuery);
        setSearchResults(res.data);
      } catch {
        setSearchResults(null);
      }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  // Клик вне dropdown — закрыть
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
        setSearchResults(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Клавиша / → открыть поиск, Esc → закрыть
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape' && searchOpen) {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults(null);
  };

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      localStorage.setItem('sidebar_collapsed', String(!prev));
      return !prev;
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const isActive = (match) => location.pathname.startsWith(match);

  return (
    <div className="layout">
      {/* Мобильный overlay */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-logo">
          <Link to={dashboardPath} className="sidebar-logo-link" title={logoText}>
            <img
              src="/logo_white.png"
              alt={logoText}
              className="sidebar-logo-img"
            />
            {!collapsed && <span className="sidebar-logo-text">{logoText}</span>}
          </Link>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-item ${isActive(item.match) ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
              title={collapsed ? item.label : undefined}
            >
              <item.icon size={20} />
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && item.path === '/notifications' && notificationCount > 0 && (
                <span className="sidebar-badge">{notificationCount}</span>
              )}
            </Link>
          ))}
        </nav>

        <button className="sidebar-toggle" onClick={toggleCollapsed}>
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          {!collapsed && <span>{t('collapse')}</span>}
        </button>
      </aside>

      {/* Основной контент */}
      <div className="layout-main">
        <header className="layout-header">
          <button className="hamburger" onClick={() => setMobileOpen(true)}>
            <Menu size={24} />
          </button>

          {/* Глобальный поиск */}
          <div className="header-search" ref={searchRef}>
            {!searchOpen ? (
              <button className="header-search-btn" onClick={() => setSearchOpen(true)} title={t('searchShortcut')}>
                <Search size={20} />
              </button>
            ) : (
              <div className="header-search-input-wrap">
                <Search size={16} className="header-search-icon" />
                <input
                  autoFocus
                  type="text"
                  className="header-search-input"
                  placeholder={t('searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim().length >= 2) {
                      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
                      closeSearch();
                    }
                  }}
                />
                <button className="header-search-close" onClick={closeSearch}>
                  <X size={16} />
                </button>
              </div>
            )}

            {searchOpen && searchLoading && (
              <div className="search-dropdown">
                <div className="search-dropdown-empty">{t('searchShort')}</div>
              </div>
            )}

            {searchOpen && searchResults && !searchLoading && (
              <div className="search-dropdown">
                {searchResults.total === 0 ? (
                  <div className="search-dropdown-empty">{t('noResults')}</div>
                ) : (
                  <>
                    {Object.entries(searchResults.results).map(([type, data]) => (
                      data.items.length > 0 && (
                        <div key={type} className="search-dropdown-group">
                          <div className="search-dropdown-group-title">
                            {t(`searchCategories.${type}`, { defaultValue: type })} ({data.count})
                          </div>
                          {data.items.map(item => (
                            <Link
                              key={`${type}-${item.id}`}
                              to={item.url}
                              className="search-dropdown-item"
                              onClick={closeSearch}
                            >
                              <div className="search-dropdown-item-title">{item.title}</div>
                              <div className="search-dropdown-item-subtitle">{item.subtitle}</div>
                            </Link>
                          ))}
                        </div>
                      )
                    ))}
                    {searchResults.total > 5 && (
                      <Link
                        to={`/search?q=${encodeURIComponent(searchQuery)}`}
                        className="search-dropdown-viewall"
                        onClick={closeSearch}
                      >
                        {t('allResults')} ({searchResults.total})
                      </Link>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="header-right">
            <button
              className="header-lang-switch"
              onClick={() => changeLanguage(i18n.language === 'uz' ? 'ru' : 'uz')}
              title={i18n.language === 'uz' ? 'Русский' : "O'zbek tili"}
            >
              <Globe size={16} />
              <span>{i18n.language === 'uz' ? 'РУ' : 'UZ'}</span>
            </button>

            <Link to="/notifications" className="header-bell">
              <Bell size={20} />
              {notificationCount > 0 && (
                <span className="header-badge">{notificationCount}</span>
              )}
            </Link>

            <Link to="/profile" className="header-user">
              <span className="header-user-name">
                {user.last_name || user.username}
              </span>
              <span className="header-user-role">
                {t(`common:roles.${user.role}`, { defaultValue: user.role || '' })}
                {user.department_name ? ` · ${user.department_name}` : ''}
              </span>
            </Link>

            <button className="header-logout" onClick={handleLogout} title={t('logout')}>
              <LogOut size={18} />
            </button>
          </div>
        </header>

        <main className="layout-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export default Layout;
